import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormSpy } from "react-final-form";
import { FormContext } from "../../context/form-context";
import { add } from "date-fns";

import {
  required,
} from "../../helpers/formValidators";

import { addDateAndTimeFields, getDurationStrict } from "./Duration";

import {
  FormLabel,
  FormInputHolder,
  FormSelect,
  FormRadio,
  FormIconChooser,
  BaseForm,
} from "../form";

import ErrorMessage from "../ErrorMessage/ErrorMessage";
import { PrimaryButton, SecondaryButton } from "../buttons";
import babyIcons from "../icons/babyIcons";
import { Condition, Error } from "../form/BaseForm";
import { OnChange } from "react-final-form-listeners";

import Pumping from "./FieldExcerpts/Pumping";
import Feeding from "./FieldExcerpts/Feeding";
import Diaper from "./FieldExcerpts/Diaper";

const DietDiaperForm = props => {
  const {
    selectedProfile,
    babies,
    profileOptions,
    dietDiaperRecordsPatch,
    dietDiaperRecordsPost,
    accountId,
    handleRecords,
    showFirstTimeSavingModal,
    showUnSavedModal,
    hideTrackerModals,
    showSaveEntryModal
  } = props;

  const defaultValues = {
    babyId: null,
    providerId: selectedProfile.id,
    weight: "100.00",
    bodyLength: "10.00",
    headCircumference: "5.0",
    date: new Date(),
    startTime: new Date(),
    endTime: add(new Date(), { minutes: 10 })
  };

  const [initialValues, setInitialValues] = useState(undefined);

  const babyIds = babies.map(baby => (baby.id));

  useEffect(() => {
    // if there's form data in the context, use that instead of using default values
    if (formContext && Object.keys(formContext.formValues).length > 0) {
      setInitialValues(formContext.formValues);
      return
    }

    const latestRecord = handleRecords('dietDiaper', null) || {};

    setInitialValues({
      ...defaultValues,
      ...latestRecord
    });
  }, []);

  const formContext = useContext(FormContext);

  const toggleWarningModal = () => {
    return formContext.isDirty ? showUnSavedModal() : hideTrackerModals();
  };

  const onSubmit = (values) => {
    if (!values) {
      return
    }

    const hasDuration =
      values.activityType === "pumping" ||
      (values.activityType === "feeding" && values.feedingType === "breast");

    const date = addDateAndTimeFields(values.date, values.startTime)
    const duration = hasDuration
      ? getDurationStrict(values.startTime, values.endTime)
      : 0;
    const endTime = hasDuration
      ? addDateAndTimeFields(values.date, values.endTime)
      : null;

    const recordBody = {
      ...values,
      date,
      duration,
      endTime,
      providerId: values.providerId === "other" ? null : values.providerId,
      otherProvider: values.providerId !== "other" ? null : values.otherProvider
    };

    if (values.id) {
      // Update existing record
      return dietDiaperRecordsPatch(
        { accountId: accountId, babyId: values.babyId, id: values.id },
        { body: JSON.stringify(recordBody) }
      )
        .then((data) => {
          hideTrackerModals();
        })
        .catch(() => <ErrorMessage />);
    } else {
      return dietDiaperRecordsPost(
        { accountId: accountId, babyId: babies.length === 1 ? babies[0].id : values.babyId },
        { body: JSON.stringify(recordBody) }
      )
        .then((data) => {
          hideTrackerModals();
        })
        .catch(() => <ErrorMessage />);
    }
  };

  const onSave = (values) => {
    if (!showFirstTimeSavingModal) {
      onSubmit(values);
    } else {
      formContext.setValues(values);
      formContext.setFunctions({
        onSubmit: onSubmit,
      });

      return showSaveEntryModal();
    }
  }

  if (!initialValues) {
    return <div></div>
  }

  return (
    <BaseForm
      onSubmit={onSave}
      initialValues={initialValues}
      className="activity-tracker__form"
    >
      <h2>Track Diet & Diaper</h2>

      {babies && babies.length > 1 && (
        <React.Fragment>
          <FormIconChooser>
            {babies.map(baby => (
              <FormRadio
                key={baby.id}
                value={baby.id}
                name="babyId"
                imageSrc={babyIcons[baby.icon]}
                validate={required}
              >
                {baby.name}
              </FormRadio>
            ))}
          </FormIconChooser>

          <OnChange name="babyId">
            {(value, previous) => {
              if (value === previous) {
                return;
              }

              const latestRecord =  handleRecords('dietDiaper', value) || {};

              setInitialValues({
                ...defaultValues,
                ...latestRecord,
                babyId: value
              });
            }}
          </OnChange>

          <Error name="babyId" />
        </React.Fragment>
      )}

      <FormLabel for="providerId">Provider</FormLabel>
      <FormSelect
        name="providerId"
        options={profileOptions}
        validate={required}
        includeOther
        otherName="otherProvider"
      />

      <FormLabel>Select Activity</FormLabel>

      <div className="form-radio__group">
        <FormRadio
          key="activity.pumping"
          name="activityType"
          value="pumping"
          validate={required}
          box
        >
          Pumping
        </FormRadio>

        <FormRadio
          key="activity.feeding"
          name="activityType"
          value="feeding"
          validate={required}
          box
        >
          Feeding
        </FormRadio>

        <FormRadio
          key="activity.diaper"
          name="activityType"
          value="diaper"
          validate={required}
          box
        >
          Diaper
        </FormRadio>
      </div>

      <Error name="activityType" />

      <Condition when="activityType" is="pumping">
        <Pumping />
      </Condition>

      <Condition when="activityType" is="feeding">
        <Feeding />
      </Condition>

      <Condition when="activityType" is="diaper">
        <Diaper />
      </Condition>

      <FormSpy subscription={{ submitting: true }}>
        {({ submitting }) => {
          return (
            <FormInputHolder>
              <SecondaryButton
                type="button"
                label="Cancel"
                onClick={toggleWarningModal}
              />

              <PrimaryButton type="submit" label="Save" disabled={submitting} />
            </FormInputHolder>
          );
        }}
      </FormSpy>

      <FormSpy
        subscription={{ dirty: true, dirtyFields: true, visited: true, values: true }}
        onChange={({ dirty, visited, values }) => {
          if (dirty) {
            let actuallyDirty = false;

            formContext.setValues(values);

            Object.keys(visited).forEach((key) => {
              if (visited[key]) {
                actuallyDirty = true;
              }
            });

            actuallyDirty ? formContext.setDirty() : formContext.notDirty();
          }
        }}
      />
    </BaseForm>
  )
};

DietDiaperForm.propTypes = {
  babies: PropTypes.arrayOf(Object),
  propfileOptions: PropTypes.arrayOf(Object),
  handleRecords: PropTypes.func,
  accountId: PropTypes.string,
  profiles: PropTypes.arrayOf(Object),
  growthRecordsPost: PropTypes.func,
  hideTrackerModals: PropTypes.func,
  dietDiaperRecordsPost: PropTypes.func,
  dietDiaperRecordsPatch: PropTypes.func,
  selectedProfile: PropTypes.object,
  showFirstTimeSavingModal: PropTypes.bool,
  growthRecordsLatest: PropTypes.func,
  showSaveEntryModal: PropTypes.func,
};

export default DietDiaperForm;



// WEBPACK FOOTER //
// ./src/components/ActivityTracker/DietDiaperForm.js